import Handlebars from "../handlebars/handlebars";
import Modal from "../modal/Modal";
import errorResponseHandler from "../errors/errorResponseHandler";
import DuplicateItemController from "../duplicateItem/DuplicateItemController";
import ConfirmController from "../confirmAction/ConfirmController";
import AddFilesController from "../addFiles/AddFilesController";
import RegisterAndSignInModalController from "../signIn/RegisterAndSignInModalController";
import ViewFilesController from "../viewFiles/ViewFilesController";
import httpService from "../_services/httpService";
import cartRowHtml from "./cartRow.hbs";
import confirmTitleHtml from "../confirmAction/confirmTitle.hbs";
import addFilesTitleHtml from "../addFiles/addFilesTitle.hbs";
import jobConfigurationHtml from "../jobConfiguration/jobConfiguration.hbs";
import viewFilesTitleHtml from "../viewFiles/viewFilesTitle.hbs";

class CartRowController {
    constructor (orderItem, container, loggedIn = false, template) {
        this.orderItem = orderItem;
        this.container = container;
        this.loggedIn = loggedIn;
        this.template = template || cartRowHtml;
        this.orderItem.initialized = false;

        this
            .enableUI()
            .render();
    }

    enableUI () {
        this.container.addEventListener('FileStatusUpdated', (e) => {
            if (!this.orderItem.initialized) {
                this.orderItem.initialized = true;
                this.orderItem.fileSessionCount = e.detail.total;
                this.triggerFileStatusUploadEvent(e.detail);
                this.render();
            }
        });

        // Newer file-status components announce readiness (non-bubbling, hence capture) and
        // expect a fetch() call instead of auto-dispatching FileStatusUpdated.
        this.container.addEventListener('FileStatusReady', () => this.hydrateFileStatus(), true);

        this.scheduleFileStatusFallback();

        this.container.addEventListener('click', this.handleClick.bind(this));
        this.container.addEventListener('focusout', this.handleNameFocusOut.bind(this));
        this.container.addEventListener('keydown', this.handleNameKeyDown.bind(this));
        this.container.addEventListener('focusout', this.handleQuantityFocusOut.bind(this));
        this.container.addEventListener('keydown', this.handleQuantityKeyDown.bind(this));

        return this;
    }

    render () {
        this.container.innerHTML = this.template(this.orderItem);

        return this;
    }

    handleClick (e) {
        // Enter quantity edit mode
        if (e.target.matches('.js-quantityDisplay')) {
            this.enterQuantityEdit(e.target);
            return;
        }

        // Duplicate an Item
        if (e.target.matches('.js-duplicateItem')) {
            this.showDuplicateItemModal()
            return;
        }

        // Remove an Item
        if (e.target.matches('.js-removeItem')) {
            this.handleConfirmItemRemoval()
            return;
        }

        // Upload / Edit / Remove Files
        if (e.target.matches('.js-openFilesModal')) {
            this.handleAddFilesDialog();
            return;
        }

        // Save for Later
        if (e.target.matches('.js-saveForLater')) {
            this.handleSaveForLater();
            return;
        }

        // Move to Cart
        if (e.target.matches('.js-moveToCart')) {
            this.handleMoveToCart();
            return;
        }

        // Configure
        if (e.target.matches('.js-configureItem')) {
            this.handleConfigureItem();
            return;
        }

        // View Files
        if (e.target.matches('.js-viewItemFiles') || e.target.matches('.js-viewItemFiles *')) {
            this.handleViewItemFiles();
            return;
        }

    }

    // The file-status component can fail to report (its fetch() throws on sessions with no
    // files). If the row is still uninitialized after a grace period, read the session status
    // directly so the row never spins forever.
    scheduleFileStatusFallback () {
        if (!this.orderItem.fileSessionId) {
            return;
        }
        setTimeout(() => {
            if (this.orderItem.initialized) {
                return;
            }
            httpService({
                url: `/api/instant-proof-service/sessions/${this.orderItem.fileSessionId}?includeFiles=true`,
                method: 'GET',
            }).then(session => {
                if (this.orderItem.initialized) {
                    return;
                }
                const total = ((session && session.files) || []).length;
                this.orderItem.initialized = true;
                this.orderItem.fileSessionCount = total;
                this.triggerFileStatusUploadEvent({ total });
                this.render();
            }).catch(() => {});
        }, 6000);
    }

    hydrateFileStatus () {
        const api = this.fileStatusApi;
        if (this.orderItem.initialized || !api || typeof api.fetch !== 'function') {
            return;
        }
        // the component announces ready during connect, before its internal API ref is
        // mounted — fetch() returns undefined then. Retry shortly; the fallback also covers us.
        const request = api.fetch();
        if (!request || typeof request.then !== 'function') {
            this._hydrateRetries = (this._hydrateRetries || 0) + 1;
            if (this._hydrateRetries <= 4) {
                setTimeout(() => this.hydrateFileStatus(), 1500);
            }
            return;
        }
        request
            .then(response => {
                if (!response || this.orderItem.initialized) {
                    return;
                }
                this.orderItem.initialized = true;
                this.orderItem.fileSessionCount = response.total;
                this.triggerFileStatusUploadEvent(response);
                this.render();
            })
            .catch(() => {});
    }

    showDuplicateItemModal () {
        const modal = new Modal({
            title: '<div class="hdg hdg_5">Duplicate Job</div>',
            controller: DuplicateItemController,
            controllerInject: [this.orderItem],
        });

        return modal.promise.then(response => this.triggerProjectUpdatedEvent(response));
    }

    handleConfirmItemRemoval (title, callback) {
        const modal = new Modal({
            showCancel: false,
            title: title || confirmTitleHtml(),
            classes: ['globalDialog_alignCenter'],
            controller: ConfirmController,
            controllerInject: ['Remove', onConfirm.bind(this), onConfirmError]
        });

        return modal.promise.then(response => {
            if (typeof callback === 'function') {
                callback(response);
            } else {
                this.triggerItemRemovedEvent(this.orderItem);
                this.triggerProjectUpdatedEvent(response);
            }
        });

        function onConfirm () {
            return Collaterate.Project.removeItem(this.orderItem);
        }

        function onConfirmError (error) {
            errorResponseHandler.generateErrorHandler()(error);
            modal.cancel();
        }
    }

    async handleAddFilesDialog (getProject) {
        const modal = new Modal({
            showCancel: Boolean(this.orderItem.fileSessionId),
            title: addFilesTitleHtml(),
            classes: ['globalDialog_wide', ...(this.orderItem.fileSessionId ? ['mix-globalDialog_noTransform'] : [])],
            controller: AddFilesController,
            controllerInject: [this.orderItem, () => {
                if (typeof getProject === 'function') {
                    return getProject();
                }
                return Collaterate.Project.getCart();
            }],
        });

        return modal.promise
            .then(response => this.triggerProjectUpdatedEvent(response))    // non instant proof uses the OK button and returns the project
            .catch(() => {                                                  // instant proof uses the cancel button
                this.fileStatusApi.fetch()
                    .then(response => {
                        this.orderItem.fileSessionCount = response.total;
                        this.triggerFileStatusUploadEvent(response);
                        this.render();
                    });
            })
    }

    // An auto-added companion (e.g. a matching envelope) belongs with the item that spawned it, so
    // saving for later always takes companions along.
    async handleSaveForLater () {
        if (this.orderItem.pending) {
            return;
        }

        this.orderItem.pending = true;
        this.render();

        if (!this.loggedIn) {
            const modal = new Modal({
                classes: ['globalDialog_flush'],
                controller: RegisterAndSignInModalController,
                controllerInject: [onSucessCallback],
            });

            function onSucessCallback (user) {
                modal.close(user);
            }

            try {
                const user = await modal.promise;
                this.loggedIn = Boolean(user);
                this.triggerUserLoggedInEvent(user);
                this.triggerItemSavedForLaterEvent(this.orderItem);
                return;
            } catch (e) {
                this.orderItem.pending = false;
                this.render();
                return;
            }
        }

        this.triggerItemSavedForLaterEvent(this.orderItem);
    }

    handleMoveToCart () {
        if (this.orderItem.pending) {
            return;
        }

        this.orderItem.pending = true;
        this.render();

        this.triggerItemMovedToCartEvent(this.orderItem);
    }

    handleConfigureItem () {
        new Modal({
            title: '<div class="hdg hdg_5">Job Configuration</div>',
            content: jobConfigurationHtml(this.orderItem),
        }).promise.catch(()=>{});
    }

    handleViewItemFiles () { // TODO: test this
        new Modal({
            title: viewFilesTitleHtml(),
            classes: ['globalDialog_wide'],
            controller: ViewFilesController,
            controllerInject: [this.orderItem],
        }).promise.catch(()=>{});
    }

    handleNameFocusOut (e) {
        if (e.target.matches('.js-editItemName')) {
            const originalName = this.orderItem.name;
            this.orderItem.name = e.target.value;

            Collaterate.Project.updateItem(this.orderItem)
                .then(response => this.triggerProjectUpdatedEvent(response))
                .catch(err => {
                    this.orderItem.name = originalName;
                    this.render();
                    errorResponseHandler.generateErrorHandler()(err);
                });
        }
    }

    handleNameKeyDown (e) {
        if (e.target.matches('.js-editItemName')) {
            switch(e.key) {
                case 'Enter':
                case 'NumpadEnter':
                    e.target.blur();
                    break;
            }
        }
    }

    // Quantity renders as plain text; clicking it swaps in the input. Clicking away either
    // commits the change (PATCH + requote — the server also keeps any auto-added companion's
    // quantity in step) or swaps back to text. Direct call because the SDK's updateItem does
    // not carry quantity.
    enterQuantityEdit (display) {
        const cell = display.closest('.orderItem-qty');
        const input = cell && cell.querySelector('.js-quantityInput');
        if (!input) {
            return;
        }
        display.hidden = true;
        input.hidden = false;
        input.boundsWarned = false;
        input.focus();
        input.select();
    }

    exitQuantityEdit (input) {
        input.value = input.dataset.current;
        const cell = input.closest('.orderItem-qty');
        const display = cell && cell.querySelector('.js-quantityDisplay');
        input.hidden = true;
        if (display) {
            display.hidden = false;
        }
    }

    handleQuantityFocusOut (e) {
        if (!e.target.matches('.js-quantityInput')) {
            return;
        }
        const input = e.target;
        const current = parseInt(input.dataset.current, 10);
        const next = parseInt(input.value, 10);
        if (!Number.isFinite(next) || next < 1 || next === current) {
            this.exitQuantityEdit(input);
            return;
        }
        // hold the product's min/max (mirrored from the product page; also enforced server-side)
        const min = parseInt(input.min, 10);
        const max = parseInt(input.max, 10);
        if ((Number.isFinite(min) && next < min) || (Number.isFinite(max) && next > max)) {
            if (!input.boundsWarned) {
                // keep editing and surface the browser's min/max message once
                input.boundsWarned = true;
                input.reportValidity();
                return;
            }
            this.exitQuantityEdit(input);
            input.boundsWarned = false;
            return;
        }
        input.boundsWarned = false;
        input.disabled = true;
        httpService({
            url: `/rest/storefront/projects/v2/${this.orderItem.projectId}/item/${this.orderItem.id}`,
            method: 'PATCH',
            contentType: 'application/merge-patch+json',
            data: JSON.stringify({ quantity: next }),
        })
            .then(() => Collaterate.Project.getCart())
            .then(cart => this.triggerProjectUpdatedEvent(cart))
            .catch(err => {
                input.disabled = false;
                this.exitQuantityEdit(input);
                errorResponseHandler.generateErrorHandler()(err);
            });
    }

    handleQuantityKeyDown (e) {
        if (e.target.matches('.js-quantityInput')) {
            switch(e.key) {
                case 'Enter':
                case 'NumpadEnter':
                    e.target.blur();
                    break;
                case 'Escape':
                    e.target.value = e.target.dataset.current;
                    e.target.blur();
                    break;
            }
        }
    }

    get fileStatusApi () {
        return document.getElementById(`file-status-${this.orderItem.id}`);
    }

    triggerProjectUpdatedEvent (project) {
        return this.container.dispatchEvent(new ProjectUpdatedEvent(project));
    }

    triggerItemRemovedEvent (item) {
        return this.container.dispatchEvent(new ItemRemovedEvent(item));
    }

    triggerUserLoggedInEvent (user) {
        return this.container.dispatchEvent(new UserLoggedInEvent(user));
    }

    triggerItemSavedForLaterEvent (item) {
        return this.container.dispatchEvent(new ItemSavedForLaterEvent(item, this.loggedIn));
    }

    triggerItemMovedToCartEvent (item) {
        return this.container.dispatchEvent(new ItemMovedToCartEvent(item));
    }
    triggerFileStatusUploadEvent (payload) {
        return this.container.dispatchEvent(new FileStatusUpdatedEvent(payload));
    }
}

class ProjectUpdatedEvent {
    constructor (project) {
        return new CustomEvent('ProjectUpdated', {
            detail: project,
            bubbles: true,
            cancelable: true,
            composed: true,
        });
    }
}

class ItemRemovedEvent {
    constructor (item) {
        return new CustomEvent('ItemRemoved', {
            detail: item,
            bubbles: true,
            cancelable: true,
            composed: true,
        });
    }
}

class UserLoggedInEvent {
    constructor (user) {
        return new CustomEvent('UserLoggedIn', {
            detail: user,
            bubbles: true,
            cancelable: true,
            composed: true,
        });
    }
}

class ItemSavedForLaterEvent {
    constructor (item, loggedIn) {
        return new CustomEvent('ItemSavedForLater', {
            detail: { item, loggedIn },
            bubbles: true,
            cancelable: true,
            composed: true,
        });
    }
}

class ItemMovedToCartEvent {
    constructor (item) {
        return new CustomEvent('ItemMovedToCart', {
            detail: item,
            bubbles: true,
            cancelable: true,
            composed: true,
        });
    }
}

class FileStatusUpdatedEvent {
    constructor (payload) {
        return new CustomEvent('FileStatusUpdated', {
            detail: payload,
            bubbles: true,
            cancelable: true,
            composed: true,
        });
    }
}

export default CartRowController;
